import { Attribute, QueryParameters, TilejsonResult } from '@carto/api-client'
import { ProtoDataset } from '@carto/kepler.gl/actions'
import { processRowObject } from '@carto/kepler.gl/processors'

import { hexToRGB } from 'features/common/utils/colors'
import { ProviderType } from 'features/connections'
import { SchemaField } from 'features/data-explorer/types'
import { Formats, MapType } from 'features/maps/types'

import { attributeHasStats, isDynamicSpatialIndexDataset } from '../builderSliceUtils'
import { MULTISELECT_FILTER_TYPE, RANGE_FILTER_TYPE } from 'features/builder/ui/builderConstants'
import { AttributeStats, AttributeType } from 'features/builder/types'
import { mapSchemaTypeToAttributeType } from 'features/builder/utils/tilestats'

interface buildKeplerDatasetProps {
  id: string
  label: string
  color: string
  connection?: string
  format?: Formats
  data: TilejsonResult
  type: MapType
  source: string
  schema?: SchemaField[]
  provider: ProviderType
  geoColumn?: string | null
  columns?: string[] | null
  aggregationExp?: string | null
  aggregationResLevel?: number | null
  accessToken?: string
  queryParameters?: QueryParameters
}

export function buildKeplerDataset({
  id,
  label,
  color,
  connection,
  format,
  data,
  type,
  source,
  schema,
  provider,
  geoColumn,
  columns,
  aggregationExp,
  aggregationResLevel,
  accessToken,
  queryParameters
}: buildKeplerDatasetProps): ProtoDataset | undefined {
  if (format !== Formats.RASTER && format !== Formats.TILEJSON) {
    return undefined
  }

  // We read this variable in carto-kepler
  const isDynamicSpatialIndex = isDynamicSpatialIndexDataset({ type, format, geoColumn })

  // Builder is nasty and modifies tilejson internally, so ensure that we only do it
  // on local copy
  const tilejson = structuredClone(data)
  if (!tilejson && schema) {
    return {
      info: { id, label, color: hexToRGB(color) },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: parseSchemaToKeplerData(schema) as any,
      metadata: {
        tilejson,
        type: MapType.tileset,
        source,
        sourceType: type,
        schema,
        connection,
        geoColumn,
        columns,
        provider,
        isDynamicSpatialIndex,
        queryParameters,
        tilestats: {
          attributes: schema
            .map(({ type, name }) => ({
              attribute: name,
              type: mapSchemaTypeToAttributeType(type) as AttributeType
            }))
            .filter((attribute) => attribute.type)
        },
        vector_layers: [
          {
            id: 'data_layer',
            fields: schema.reduce((acc, { name, type }) => {
              const attributeType = mapSchemaTypeToAttributeType(type)
              if (attributeType) {
                acc[name] = attributeType
              }
              return acc
            }, {} as Record<string, AttributeType>)
          }
        ]
      }
    }
  }
  if (!tilejson) throw new Error('Tilejson is required to build a kepler dataset')

  processTilestats(tilejson)

  const tilestats = tilejson.tilestats?.layers[0] || getDefaultTilestats(tilejson)
  const bounds = tilejson.bounds
  const center = tilejson.center
  const vector_layers = tilejson.vector_layers
  const fractionDroppedPerZoom = tilejson.fraction_dropped_per_zoom || []
  const raster_metadata = tilejson.raster_metadata

  return {
    info: { id, label, color: hexToRGB(color) },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: parseTilesetToKeplerData(tilejson) as any,
    metadata: {
      tilejson,
      tilestats,
      bounds,
      center,
      vector_layers,
      raster_metadata,
      type: type === MapType.raster ? MapType.raster : MapType.tileset,
      source,
      sourceType: type,
      dataresolution: tilejson.dataresolution,
      aggregationExp,
      aggregationResLevel,
      scheme: tilejson.scheme,
      schema,
      connection,
      geoColumn,
      columns,
      provider,
      isDynamicSpatialIndex,
      fractionDroppedPerZoom,
      accessToken,
      queryParameters
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getDefaultTilestats(tileset: TilejsonResult) {
  return {
    attributes: [],
    geometry: tileset.vector_layers?.[0]?.geometry_type
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function processTilestats(tilejson: TilejsonResult) {
  let attributes = tilejson.tilestats?.layers[0]?.attributes as AttributeStats[]

  if (!attributes) {
    return
  }

  // Static spatial index tilesets return the stats by resolution so we get the
  // global resolution and ignore the rest. We modify the original tilejson to keep the compatibility
  attributes = attributes.map((attribute) => {
    return Object.entries(attribute).reduce((prev, [key, value]) => {
      if (Object.prototype.hasOwnProperty.call(value, 'global')) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        prev[key] = (value as any).global
        return prev
      }
      prev[key] = value
      return prev
    }, {} as typeof attribute)
  })

  // Static tilesets, sometimes encode some stats as strings, convert them to number
  attributes = attributes.map((attribute) => {
    if (attribute.type === 'Number') {
      ;['sum', 'min', 'max', 'avg'].forEach((stat) => {
        if (typeof attribute[stat] === 'string') {
          attribute = { ...attribute, [stat]: Number.parseFloat(attribute[stat]) }
        }
      })
      if (attribute.quantiles) {
        attribute.quantiles = Object.entries(attribute.quantiles).reduce((result, [key, values]) => {
          result[key] = values.map(Number)
          return result
        }, {} as Record<number, number[]>)
      }
    }
    return attribute
  })

  tilejson.tilestats.layers[0].attributes = attributes as Attribute[]
}

function parseSchemaToKeplerData(schema: SchemaField[]) {
  const parsedAttributes = schema.reduce((acc, field) => {
    const { name, type } = field
    switch (type) {
      case 'string':
        // this fake value for kepler to detect it as actual string
        acc[name] = 'sampleString'
        break
      case 'number':
        acc[name] = 0
        break
      case 'boolean':
        acc[name] = false
        break
      case 'timestamp':
        acc[name] = new Date().toISOString()
        break
    }
    return acc
  }, {} as Record<string, string | number | boolean | Date>)

  const data = processRowObject([{ ...parsedAttributes }])

  return data
}

function getSampleRowVectorTileset(tilejson: TilejsonResult) {
  const attributes = (tilejson.vector_layers && tilejson.vector_layers[0].fields) || {}
  return Object.keys(attributes).reduce((acc, current) => {
    const type = attributes[current].toLocaleLowerCase()

    switch (type) {
      case 'string':
        // this fake value for kepler to detect it as actual string
        acc[current] = 'sampleString'
        break
      case 'number':
        acc[current] = 0
        break
      case 'boolean':
        acc[current] = false
        break
      case 'timestamp':
        acc[current] = new Date().toISOString()
        break
    }
    return acc
  }, {} as Record<string, string | number | boolean | Date>)
}

function getSampleRowRaster(tilejson: TilejsonResult) {
  return tilejson?.raster_metadata?.bands.reduce((acc, band) => {
    acc[band.name] = band.stats.min || 0
    return acc
  }, {} as Record<string, number>)
}

function parseTilesetToKeplerData(tilejson: TilejsonResult) {
  const stats = tilejson.tilestats?.layers[0].attributes
  const parsedAttributes = tilejson.raster_metadata ? getSampleRowRaster(tilejson) : getSampleRowVectorTileset(tilejson)

  const data = processRowObject([{ ...parsedAttributes }])

  data?.fields.map((f) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const stat = stats?.find((s: any) => s.attribute === f.name) as AttributeStats | undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let domain: any[] = []
    let value: Array<string | number> = []
    let type
    let gpu
    if (stat && attributeHasStats(stat)) {
      if (stat.type === AttributeType.String || stat.type === AttributeType.Boolean) {
        gpu = false
        ;(domain = stat.categories.map((c: { category: string }) => c.category)), (type = MULTISELECT_FILTER_TYPE)
      } else if (stat.type === AttributeType.Number || stat.type === AttributeType.Timestamp) {
        gpu = true
        domain = [stat.min, stat.max]
        value = [...domain]
        type = RANGE_FILTER_TYPE
      }
    }
    f.filterProps = {
      fieldType: f.type,
      domain,
      value,
      histogram: null,
      gpu,
      step: 1,
      type
    }
    return f
  })

  return data
}
